import dayjs from "dayjs";
import React from "react";
import { userPath } from "../../../../../../routes";
import { HelperImageFile, SharedTag } from "../../../../../shared/lib/types";
import { PostsShowProps } from "../../../../posts/lib/types";
import { HelperRealNamePostResourceAuthor, SharedCurrentUser } from "../../../lib/types";
import { isVisitableUserProfile } from "../../../lib/userProfileUtils";

import LinkedText from "../../ui/LinkedText";
import Tag from "../../ui/Tag";
import FilesWithoutImage from "../OldFilesWithoutImage";
import PostNewBadge from "../PostNewBadge";
import PostResourceAvatar from "../PostResourceAvatar";
import PostResourceDisplayName from "../PostResourceDisplayName";
import Questionnaire from "../Questionnaire";
import { Divider, Box, Flex, Grid, HStack, Heading, Image, Link, Stack, Text } from "@chakra-ui/react";
import { Actions } from "./Actions";
import { ImagesSliderWrapper } from "../ImagesSliderWrapper";

const PostDetail = ({
  post,
  currentUser,
  preview = false,
}: {
  post: PostsShowProps;
  currentUser: SharedCurrentUser;
  preview?: boolean;
}) => {
  const props = isVisitableUserProfile(post.author, currentUser)
    ? {
        as: Link,
        href: userPath((post.author as HelperRealNamePostResourceAuthor).code),
        cursor: "pointer",
        target: "_blank",
        rel: "noreferrer",
      }
    : {};
  // 画像URLはユニーク、最大5枚
  const imageUrls = post.image_files.map((file: HelperImageFile) => file.url);

  return (
    <Box bgColor="white" key={post.code} borderRadius={8} border="1px solid rgba(0, 0, 0, 0.10)" position="relative">
      <Box py={{ base: 4, sm: 7 }} px={{ base: 5, sm: 9 }}>
        <Flex justifyContent="space-between" direction={{ base: "column", md: "row" }}>
          <HStack
            gap={3}
            textDecoration="none"
            color="inherit"
            {...props}
            w="fit-content"
            // 52px = 三点リーダーの大きさ + rightの値
            // 20px = カードの中のpadding
            maxW="calc(100% - 52px + 20px)"
            wordBreak="break-word"
          >
            <PostResourceAvatar w={12} h={12} author={post.author} />
            <Stack gap={0}>
              <PostResourceDisplayName author={post.author} />
              <HStack color="#848484" fontSize="xs" wrap="wrap" rowGap={0} columnGap={2.5}>
                <Text color="#848484" fontSize="xs">
                  投稿日：{dayjs(post.created_at).format("L LT")}
                </Text>
                {post.updated_at !== "" && post.created_at !== post.updated_at && (
                  <Text color="#848484" fontSize="xs">
                    更新日：{dayjs(post.updated_at).format("L LT")}
                  </Text>
                )}
              </HStack>
            </Stack>
          </HStack>
          {!preview && <Actions post={post} currentUser={currentUser} />}
        </Flex>
        <Heading as="h1" fontSize={{ base: "md", sm: "xl" }} mt={{ base: 2, sm: 4 }} lineHeight="1.5">
          {!preview && post.show_new_indicator && <PostNewBadge mr={2} />}
          {post.title}
        </Heading>
        <Flex gap={1.5} wrap="wrap" mt={1}>
          {post.tags.map((tag: SharedTag) => (
            <Tag key={tag.id}>{tag.name}</Tag>
          ))}
        </Flex>
        <Divider borderColor="#8DCCBE" mt={6} />
        <Box mt={7} fontSize={{ base: "sm", sm: "md" }} whiteSpace="pre-wrap">
          <LinkedText>{post.content}</LinkedText>
        </Box>
        {post.files_without_image.length !== 0 && (
          <Box mt={4}>
            <FilesWithoutImage files={post.files_without_image} />
          </Box>
        )}
        {post.image_files.length !== 0 && (
          <Box mt={6} mb={4}>
            <ImagesSliderWrapper urls={imageUrls}>
              {({ openSlider }) => {
                const ImageItem = ({ url, index }: { url: string; index: number }) => (
                  <Image
                    src={url}
                    borderRadius={8}
                    objectFit="cover"
                    w="full"
                    h="full"
                    cursor="pointer"
                    onClick={() => openSlider(index)}
                  />
                );

                if (imageUrls.length === 1) {
                  return (
                    <Box aspectRatio={4 / 3.2}>
                      <ImageItem url={imageUrls[0]} index={0} />
                    </Box>
                  );
                }

                if (imageUrls.length === 2) {
                  return (
                    <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                      {imageUrls.map((url, i) => (
                        <Box key={url} h={{ base: "100px", sm: "260px" }}>
                          <ImageItem url={url} index={i} />
                        </Box>
                      ))}
                    </Grid>
                  );
                }

                if (imageUrls.length === 3) {
                  return (
                    <Grid templateColumns="repeat(3, 1fr)" gap={2}>
                      {imageUrls.map((url, i) => (
                        <Box key={url} h={{ base: "100px", sm: "260px" }}>
                          <ImageItem url={url} index={i} />
                        </Box>
                      ))}
                    </Grid>
                  );
                }

                if (imageUrls.length === 4) {
                  return (
                    <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                      {imageUrls.map((url, i) => (
                        <Box key={url} h={{ base: "100px", sm: "260px" }}>
                          <ImageItem url={url} index={i} />
                        </Box>
                      ))}
                    </Grid>
                  );
                }

                // 5枚（最大枚数）
                return (
                  <Stack gap={2}>
                    <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                      {imageUrls.slice(0, 2).map((url, i) => (
                        <Box key={url} h={{ base: "100px", sm: "260px" }}>
                          <ImageItem url={url} index={i} />
                        </Box>
                      ))}
                    </Grid>
                    <Grid templateColumns="repeat(3, 1fr)" gap={2}>
                      {imageUrls.slice(2, 5).map((url, i) => (
                        <Box key={url} h={{ base: "100px", sm: "260px" }}>
                          <ImageItem url={url} index={i + 2} />
                        </Box>
                      ))}
                    </Grid>
                  </Stack>
                );
              }}
            </ImagesSliderWrapper>
          </Box>
        )}
        {post.questionnaire && (
          <Box mt={5}>
            <Questionnaire questionnaire={post.questionnaire} currentUser={currentUser} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PostDetail;
